import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../CoreFunctions';

const CashAssets = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [closeDate, setCloseDate] = useState('');
  const [lenderChoices, setLenderChoices] = useState([]);
  const [viewMode, setViewMode] = useState('tiles');
  // Collapse state (tiles view)
  const [checkingExpanded, setCheckingExpanded] = useState(true);
  const [savingsExpanded, setSavingsExpanded] = useState(true);
  const [cdExpanded, setCdExpanded] = useState(true);
  const [form, setForm] = useState({
    lender: '',
    account_type: 'checking',
    account_number: '',
    opened_date: '',
    maturity_date: '',
    interest_rate_apr: ''
  });

  const apiBase = '/api/balance_sheet/assets/cash_accounts';

  const loadAccounts = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(apiBase + '/');
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const loadLenders = async () => {
    try {
      const res = await axios.get('/api/balance_sheet/financing/lender-choices/');
      setLenderChoices(res.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadAccounts();
    loadLenders();
  }, []);

  // Keyboard shortcuts for collapsing/expanding (only in tiles view)
  useEffect(() => {
    if (viewMode !== 'tiles') return;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || e.ctrlKey || e.metaKey || e.altKey) return;
      
      if (e.key === 'c' || e.key === 'C') {
        setCheckingExpanded(prev => !prev);
      } else if (e.key === 's' || e.key === 'S') {
        setSavingsExpanded(prev => !prev);
      } else if (e.key === 'd' || e.key === 'D') {
        setCdExpanded(prev => !prev);
      } else if (e.key === 'a' || e.key === 'A') {
        const anyCollapsed = !checkingExpanded || !savingsExpanded || !cdExpanded;
        setCheckingExpanded(anyCollapsed);
        setSavingsExpanded(anyCollapsed);
        setCdExpanded(anyCollapsed);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewMode, checkingExpanded, savingsExpanded, cdExpanded]);

  const resetForm = () => setForm({ lender: '', account_type: 'checking', account_number: '', opened_date: '', maturity_date: '', interest_rate_apr: '' });

  const createAccount = async (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      lender: form.lender || null, // expects id or null
      account_type: form.account_type,
      account_number: form.account_number,
      opened_date: form.opened_date || null,
    };

    try {
      await axios.post(apiBase + '/', payload);
      await loadAccounts(); // auto-refresh list
      setShowForm(false);
      resetForm();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const accountTypeLabel = (t) => ({ checking: 'Checking', savings: 'Savings', cd: 'CD' }[t] || t);

  const categorized = useMemo(() => {
    const groups = { checking: [], savings: [], cd: [] };
    accounts.forEach(a => {
      if (groups[a.account_type]) groups[a.account_type].push(a);
      else groups.checking.push(a); // fallback
    });
    // Sort each group by balance descending
    Object.keys(groups).forEach(k => {
      groups[k].sort((a, b) => (b.latest_balance || 0) - (a.latest_balance || 0));
    });
    return groups;
  }, [accounts]);

  const totals = useMemo(() => {
    const t = { checking: 0, savings: 0, cd: 0, all: 0 };
    accounts.forEach(a => {
      const bal = parseFloat(a.latest_balance) || 0;
      if (t[a.account_type] !== undefined) t[a.account_type] += bal;
      t.all += bal;
    });
    return t;
  }, [accounts]);

  const HeaderMetrics = ({ groupTotals, isTicker }) => {
    const metricsStyle = isTicker
      ? { display: 'flex', alignItems: 'baseline' }
      : { display: 'grid', gridTemplateColumns: 'repeat(4, 180px)', gap: 16, alignItems: 'baseline' };

    const checkingSavingsTotal = (groupTotals.checking || 0) + (groupTotals.savings || 0);

    return (
      <div className={`header-metrics ${isTicker ? 'ticker' : ''}`} style={metricsStyle}>
        <div className="header-metric">
          <span className="metric-label">{isTicker ? 'Total' : 'Total Checking + Savings'}</span>{' '}
          <span className="metric-value">{formatCurrency(checkingSavingsTotal, 0)}</span>
        </div>
        <div className="header-metric">
          <span className="metric-label">{isTicker ? 'Checking' : 'Total Checking'}</span>{' '}
          <span className="metric-value">{formatCurrency(groupTotals.checking, 0)}</span>
        </div>
        <div className="header-metric">
          <span className="metric-label">{isTicker ? 'Savings' : 'Total Savings'}</span>{' '}
          <span className="metric-value">{formatCurrency(groupTotals.savings, 0)}</span>
        </div>
        {groupTotals.cd > 0 && (
          <div className="header-metric">
            <span className="metric-label">{isTicker ? 'CDs' : 'Total CDs'}</span>{' '}
            <span className="metric-value">{formatCurrency(groupTotals.cd, 0)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page cash-assets-page">
      <div className="page-header-row" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', rowGap: 8, marginBottom: '20px', width: '100%', justifyContent: 'flex-start', minWidth: 0 }}>
        <div className="header-title-container" style={{ flexShrink: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Cash Accounts</h1>
        </div>
        {viewMode === 'tiles' && (
          <div className="header-ticker" style={{ display: 'flex', alignItems: 'baseline', borderLeft: '1px solid #444' }}>
            <HeaderMetrics groupTotals={totals} isTicker={true} />
          </div>
        )}
        <div className="header-breadcrumb-container" style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <Link to="/balance_sheet/assets/" style={{ whiteSpace: 'nowrap' }}> All Assets</Link>
        </div>
      </div>

      <div className="tabs-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div className="tab-bar">
          <button className={`tab-btn ${viewMode === 'tiles' ? 'active' : ''}`} onClick={() => setViewMode('tiles')}>Tiles</button>
          <button className={`tab-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>Table</button>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>Create New Cash Account</button>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {viewMode === 'table' && (
            <div className="card" style={{ marginTop: 10 }}>
              <div className="card-body">
                {accounts.length === 0 ? (
                  <p>No cash accounts yet. Click "Create New Cash Account" to add one.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Lender</th>
                          <th>Type</th>
                          <th>Account #</th>
                          <th>Opened</th>
                          <th>Balance</th>
                          <th>Balance as of date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map(acc => {
                          const asOf = acc.balance_as_of_date || '—';
                          const isClosed = !!acc.closed;
                          return (
                            <tr key={acc.id}>
                              <td><Link to={`/balance_sheet/assets/cash_assets/${acc.id}`}>{acc.id}</Link></td>
                              <td>{acc.lender_name || '—'}</td>
                              <td>{accountTypeLabel(acc.account_type)}</td>
                              <td>{acc.account_number}</td>
                              <td>{acc.opened_date || '—'}</td>
                              <td>{formatCurrency(acc.latest_balance)}</td>
                              <td>{asOf}</td>
                              <td>
                                {isClosed ? (
                                  <span title={`Closed ${acc.closed_date || ''}`}>Closed</span>
                                ) : (
                                  <button className="btn btn-sm btn-warning" onClick={() => { setSelectedAccount(acc); setCloseDate(''); setShowCloseForm(true); }}>Close</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewMode === 'tiles' && (
            <div className="tiles-container">
              {Object.entries(categorized).map(([type, group]) => {
                if (group.length === 0) return null;
                const groupTotal = group.reduce((sum, a) => sum + (parseFloat(a.latest_balance) || 0), 0);
                const isExpanded = type === 'checking' ? checkingExpanded : type === 'savings' ? savingsExpanded : cdExpanded;
                const toggleExpanded = () => {
                  if (type === 'checking') setCheckingExpanded(!checkingExpanded);
                  else if (type === 'savings') setSavingsExpanded(!savingsExpanded);
                  else if (type === 'cd') setCdExpanded(!cdExpanded);
                };

                return (
                  <div key={type} className="tiles-section">
                    <div className="tiles-section-header">
                      <div className="header-title-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          className="collapse-btn"
                          aria-expanded={isExpanded}
                          title={(isExpanded ? 'Collapse ' : 'Expand ') + accountTypeLabel(type) + ' accounts'}
                          onClick={toggleExpanded}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                        <h2 className="tiles-section-title">{accountTypeLabel(type)} Accounts</h2>
                      </div>
                      <div className="header-metrics">
                        <div className="header-metric">
                          <span className="metric-label">Group Total</span>
                          <span className="metric-value">{formatCurrency(groupTotal, 0)}</span>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="tiles-grid">
                        {group.map(acc => (
                          <Link key={acc.id} to={`/balance_sheet/assets/cash_assets/${acc.id}`} className="tile-card-link">
                            <div className="tile-card">
                              <div className="tile-icon">{type === 'savings' ? '💰' : type === 'cd' ? '🔒' : '💳'}</div>
                              <div className="tile-title">{acc.lender_name || 'Unknown Lender'}</div>
                              <div className="tile-subtitle">*{String(acc.account_number).slice(-4)}</div>
                              <div className="tile-metrics">
                                <div>
                                  <span className="metric-label">Balance</span>
                                  <span className="metric-value">{formatCurrency(acc.latest_balance, 2)}</span>
                                </div>
                                <div>
                                  <span className="metric-label">As of</span>
                                  <span className="metric-value" style={{ fontSize: '0.9em' }}>{acc.balance_as_of_date || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {accounts.length === 0 && <p>No cash accounts yet. Click "Create New Cash Account" to add one.</p>}
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-window">
            <div className="modal-header">
              <h3 className="modal-title">Create Cash Account</h3>
              <button type="button" className="modal-close" onClick={() => { setShowForm(false); resetForm(); }} aria-label="Close">×</button>
            </div>
            <form onSubmit={createAccount} className="modal-body">
              <div className="form-row">
                <label>Account Type</label>
                <select value={form.account_type} onChange={e => setForm(prev => ({...prev, account_type: e.target.value}))}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="cd">CD</option>
                </select>
              </div>

              <div className="form-row">
                <label>Lender</label>
                <select value={form.lender} onChange={e => setForm(prev => ({...prev, lender: e.target.value}))}>
                  <option value="">— Optional —</option>
                  {lenderChoices.map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label>Account Number</label>
                <input type="text" value={form.account_number} onChange={e => setForm(prev => ({...prev, account_number: e.target.value}))} required />
              </div>

              <div className="form-row">
                <label>Opened Date</label>
                <input type="date" value={form.opened_date} onChange={e => setForm(prev => ({...prev, opened_date: e.target.value}))} />
              </div>

              {form.account_type === 'cd' && (
                <>
                  <div className="form-row">
                    <label>Maturity Date</label>
                    <input type="date" value={form.maturity_date} onChange={e => setForm(prev => ({...prev, maturity_date: e.target.value}))} />
                  </div>
                  <div className="form-row">
                    <label>Interest Rate (APR)</label>
                    <input type="number" step="0.0001" value={form.interest_rate_apr} onChange={e => setForm(prev => ({...prev, interest_rate_apr: e.target.value}))} />
                  </div>
                </>
              )}

              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCloseForm && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-window">
            <div className="modal-header">
              <h3 className="modal-title">Close Account</h3>
              <button type="button" className="modal-close" onClick={() => { setShowCloseForm(false); setSelectedAccount(null); setCloseDate(''); }} aria-label="Close">×</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await axios.post(`${apiBase}/${selectedAccount.id}/close/`, { date_closed: closeDate });
                await loadAccounts();
                setShowCloseForm(false);
                setSelectedAccount(null);
                setCloseDate('');
              } catch (e) {
                setError(e.response?.data?.detail || e.message);
              }
            }} className="modal-body">
              <div className="form-row">
                <label>Account</label>
                <input type="text" value={`${selectedAccount.lender_name || ''} ${accountTypeLabel(selectedAccount.account_type)} *${String(selectedAccount.account_number).slice(-4)}`} readOnly />
              </div>
              <div className="form-row">
                <label>Closed Date</label>
                <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} required />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowCloseForm(false); setSelectedAccount(null); setCloseDate(''); }}>Cancel</button>
                <button type="submit" className="btn btn-warning">Close Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .cash-assets-page {
          margin-left: clamp(20px, 4vw, 40px);
          margin-right: clamp(20px, 4vw, 40px);
        }
        .page-header-row {
          margin-top: 20px;
        }
        .header-title-container h1 {
          font-size: clamp(24px, 3.5vw, 44px);
        }
        .header-ticker {
          padding-left: clamp(10px, 1.5vw, 24px);
          margin-left: clamp(5px, 0.8vw, 12px);
          transition: all 0.2s ease;
        }
        .header-metrics.ticker .header-metric {
          margin-right: 20px;
        }
        .header-metrics.ticker .metric-label {
          font-size: 14px;
          color: #bbbbbb;
        }
        .header-metrics.ticker .metric-value {
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
        }
        .tab-bar {
          display: inline-flex;
          align-items: flex-end;
          gap: 4px;
          border-bottom: 1px solid #374151;
        }
        .tab-btn {
          background: #111827;
          color: #e5e7eb;
          border: 1px solid #374151;
          border-bottom-color: #374151;
          padding: 6px 14px;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          cursor: pointer;
          font-size: 14px;
        }
        .tab-btn.active {
          background: #1f2937;
          border-color: #4b5563;
          border-bottom-color: transparent;
          color: #ffffff;
          position: relative;
          top: 1px;
        }
        .tiles-container {
          width: 100%;
        }
        .tiles-section {
          margin-bottom: 40px;
        }
        .tiles-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #2a2a2a;
          padding-bottom: 8px;
          margin-bottom: 16px;
        }
        .tiles-section-title {
          font-size: 22px;
          color: #dddddd;
          margin: 0;
        }
        .tiles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
        .tile-card-link {
          text-decoration: none;
          color: inherit;
        }
        .tile-card {
          background: #1e1e1e;
          border: 1px solid #2a2a2a;
          border-radius: 10px;
          padding: 20px;
          transition: transform 0.2s, box-shadow 0.2s;
          height: 100%;
        }
        .tile-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        }
        .tile-icon {
          font-size: 32px;
          margin-bottom: 12px;
        }
        .tile-title {
          font-size: 18px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 4px;
        }
        .tile-subtitle {
          font-size: 14px;
          color: #aaaaaa;
          margin-bottom: 16px;
        }
        .tile-metrics {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .metric-label {
          display: block;
          font-size: 12px;
          color: #bbbbbb;
          text-transform: uppercase;
        }
        .metric-value {
          display: block;
          font-size: 16px;
          font-weight: 600;
          color: #ffffff;
        }
        .collapse-btn {
          width: 20px;
          height: 20px;
          padding: 0;
          line-height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #111827;
          color: #e5e7eb;
          border: 1px solid #374151;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 700;
          user-select: none;
        }
        .collapse-btn:hover {
          background: #1f2937;
        }
        .collapse-btn:focus {
          outline: 2px solid #2563eb;
          outline-offset: 1px;
        }
        .header-breadcrumb-container a {
          color: #60a5fa;
          text-decoration: none;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default CashAssets;
